import { Box, Stack } from "@mui/material";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_CHANNEL_MIX_BAR,
  CHART_KEY_DASHBOARD_CHANNEL_MIX_PIE,
  CHART_KEY_DASHBOARD_CHANNEL_MIX_TABLE,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";
import {
  ApiChartAttribute,
  ApiChartLayout,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { useMemo, useState } from "react";
import {
  dashboardChartKey,
  revenueOrConversions,
  useIsEcommerceProject,
} from "features/dashboard/utils/ecommerceMetrics";

interface IChannelMixTabProps {
  attributes: ApiChartAttribute[];
}

function ChannelMixTab({ attributes }: IChannelMixTabProps) {
  const isEcommerce = useIsEcommerceProject();
  const outcomeMetric = revenueOrConversions(isEcommerce);
  const [pieMetric, setPieMetric] = useState<ApiMetric>(
    ApiMetric.METRIC_SESSIONS_COUNT,
  );

  const pieAvailableMetrics = useMemo(
    () =>
      [
        ApiMetric.METRIC_SESSIONS_COUNT,
        ApiMetric.METRIC_PAGEVIEWS_COUNT,
        ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
        ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
        ...(isEcommerce
          ? [ApiMetric.METRIC_SALES_COUNT, ApiMetric.METRIC_REVENUE]
          : [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT]),
      ] as ApiMetric[],
    [isEcommerce],
  );

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%" }}
      >
        <Box sx={{ height: 400, width: { xs: "100%", md: "33.333%" }, flexShrink: 0 }}>
          <StandardAnalyticsCard
            chartKey={dashboardChartKey(
              CHART_KEY_DASHBOARD_CHANNEL_MIX_PIE,
              isEcommerce,
            )}
            staticAttributes={attributes}
            onChangeMetric={(m) => setPieMetric(m)}
            chart={{
              name: "Channel Performance by",
              dimension: [ApiDimension.DIMENSION_CHANNEL],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [pieMetric],
                },
              ],
              displayOptions: {
                noFilters: true,
                showTotals: true,
                availableMetrics: pieAvailableMetrics,
              },
            }}
          />
        </Box>

        <Box sx={{ height: 400, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={dashboardChartKey(
              CHART_KEY_DASHBOARD_CHANNEL_MIX_BAR,
              isEcommerce,
            )}
            staticAttributes={attributes}
            chart={{
              name: isEcommerce
                ? "Overall Revenue by Top 5 Channels"
                : "Overall Conversions by Top 5 Channels",
              dimension: [ApiDimension.DIMENSION_CHANNEL],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 5,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [outcomeMetric],
                  chart: {
                    [outcomeMetric]: ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                layout: ApiChartLayout.VERTICAL,
                noLegend: true,
              },
            }}
          />
        </Box>
      </Stack>

      <Box sx={{ height: table5Rows, flexShrink: 0, width: "100%" }}>
        <StandardAnalyticsCard
          chartKey={dashboardChartKey(
            CHART_KEY_DASHBOARD_CHANNEL_MIX_TABLE,
            isEcommerce,
          )}
          staticAttributes={attributes}
          chart={{
            name: "Channel Mix by ",
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            comparison: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ...(isEcommerce
                    ? [ApiMetric.METRIC_SALES_COUNT]
                    : []),
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  outcomeMetric,
                ],
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              noFilters: true,
              rowsPerPage: 0,
              showTotals: true,
              availableDimensions: [
                ApiDimension.DIMENSION_CHANNEL,
                ApiDimension.DIMENSION_SOURCE,
                ApiDimension.DIMENSION_MEDIUM,
              ],
            },
          }}
        />
      </Box>
    </>
  );
}

export default ChannelMixTab;
